import React from 'react';
import './TourQuotationPDF.css';
import { toast } from './Toast';

const TourQuotationPDF = ({ quotation, lead, onClose, onSendToSales, onEdit }) => {
  const handlePrint = () => {
    document.querySelector('.quotation-pages')?.classList.remove('itinerary-only-mode');
    document.body.classList.add('printing-pdf');
    window.onafterprint = () => document.body.classList.remove('printing-pdf');
    setTimeout(() => window.print(), 50);
  };

  const handleDownload = () => {
    document.querySelector('.quotation-pages')?.classList.remove('itinerary-only-mode');
    document.body.classList.add('printing-pdf');
    window.onafterprint = () => document.body.classList.remove('printing-pdf');
    setTimeout(() => window.print(), 50);
  };

  const handlePrintItineraryOnly = () => {
    document.querySelector('.quotation-pages')?.classList.add('itinerary-only-mode');
    document.body.classList.add('printing-pdf');
    window.onafterprint = () => {
      document.body.classList.remove('printing-pdf');
      document.querySelector('.quotation-pages')?.classList.remove('itinerary-only-mode');
    };
    setTimeout(() => window.print(), 100);
  };

  const handleSendToSales = async () => {
    if (!onSendToSales) {
      toast.warning('Send to Sales functionality not available');
      return;
    }

    // Get sales person name from various possible fields
    const salesPersonName = lead?.createdByName ||
      lead?.assignedToName ||
      lead?.salesPersonName ||
      quotation?.salesPerson?.name ||
      'Sales Team';

    if (window.confirm(`Send this quotation to ${salesPersonName}?`)) {
      await onSendToSales();
    }
  };

  if (!quotation) return null;

  const firstItem = quotation.items?.[0] || {};

  // Debug logging
  console.log('📄 TourQuotationPDF - Full Quotation Data:', quotation);
  console.log('📦 Items array length:', quotation.items?.length);
  console.log('📦 First item:', firstItem);

  // Prefer items[0] data over flat top-level fields (items[0] is always latest saved data)
  const hasItems = quotation.items && quotation.items.length > 0;

  const customerName = quotation.customerName;
  const destination = hasItems ? (firstItem.destination || quotation.destination) : quotation.destination;
  const startDate = hasItems ? (firstItem.travelDates?.startDate || quotation.startDate) : quotation.startDate;
  const duration = hasItems ? (firstItem.duration || quotation.duration) : quotation.duration;
  const totalPax = hasItems ? (firstItem.totalPax || quotation.totalPax) : quotation.totalPax;
  const pax = totalPax;
  const tripId = quotation.tripId;
  const packageName = quotation.packageName;
  const pricePerPerson = hasItems ? (firstItem.pricePerPerson || quotation.pricePerPerson) : quotation.pricePerPerson;
  const totalPrice = hasItems ? (firstItem.totalPrice || quotation.totalPrice) : quotation.totalPrice;
  const pricePerAdult = hasItems ? (firstItem.pricePerAdult || quotation.pricePerAdult || pricePerPerson) : (quotation.pricePerAdult || pricePerPerson);
  const pricePerChild = hasItems ? (firstItem.pricePerChild || quotation.pricePerChild || pricePerAdult) : (quotation.pricePerChild || pricePerAdult);
  const airfarePerPerson = hasItems ? (firstItem.airfarePerPerson || quotation.airfarePerPerson || 0) : (quotation.airfarePerPerson || 0);
  const packageImage = hasItems ? (firstItem.images?.[0] || quotation.packageImage) : quotation.packageImage;

  // For arrays: prefer items[0] if it has data, otherwise fall back to top-level
  const hotels = hasItems && firstItem.hotels?.length > 0 ? firstItem.hotels : (quotation.hotels || []);
  const transportation = hasItems ? (firstItem.transportation || quotation.transportation) : quotation.transportation;
  const itinerary = hasItems && firstItem.itinerary?.length > 0 ? firstItem.itinerary : (quotation.itinerary || []);
  // Helper: detect HTML content
  const isHTML = (str) => typeof str === 'string' && /<[a-z][\s\S]*>/i.test(str);

  // Helper: always return an array. If value is an HTML string, keep it whole (don't split).
  const toArray = (val, splitChar = /\n/) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) {
      // If it contains HTML tags, keep as single item - don't split
      if (isHTML(val)) return [val];
      return val.split(splitChar).map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Helper: render a list section — handles both plain strings and HTML strings
  const renderListContent = (items, bulletClass) => {
    if (!items || items.length === 0) return null;
    // If any item contains HTML, render the whole thing as HTML
    if (items.some(item => isHTML(item))) {
      const combinedHTML = items.join('');
      return (
        <div
          className="ql-content html-list-content"
          dangerouslySetInnerHTML={{ __html: combinedHTML }}
        />
      );
    }
    // Plain text — render as styled bullet list
    return items.map((item, index) => (
      <li key={index}>
        <span className={bulletClass}>●</span> {item}
      </li>
    ));
  };

  const inclusions = toArray(hasItems && firstItem.inclusions?.length > 0 ? firstItem.inclusions : quotation.inclusions);
  const exclusions = toArray(hasItems && firstItem.exclusions?.length > 0 ? firstItem.exclusions : quotation.exclusions);
  const termsAndConditions = toArray(hasItems && firstItem.termsAndConditions?.length > 0 ? firstItem.termsAndConditions : quotation.termsAndConditions);
  const importantNotes = toArray(hasItems && firstItem.importantNotes?.length > 0 ? firstItem.importantNotes : quotation.importantNotes);
  const salesPerson = hasItems ? (firstItem.salesPerson || quotation.salesPerson) : quotation.salesPerson;
  const companyInfo = hasItems ? (firstItem.companyInfo || quotation.companyInfo) : quotation.companyInfo;

  // Final debug logging
  console.log('✅ Extracted Data:', {
    destination,
    itineraryCount: itinerary?.length,
    hotelsCount: hotels?.length,
    inclusionsCount: inclusions?.length,
    exclusionsCount: exclusions?.length,
    termsCount: termsAndConditions?.length,
    importantNotesCount: importantNotes?.length,
    hasItems
  });

  console.log('📋 Terms & Conditions in PDF:', termsAndConditions);

  return (
    <div className="quotation-modal-overlay">
      <div className="quotation-modal-container">
        {/* Action Buttons */}
        <div className="quotation-actions no-print">
          {onSendToSales && lead && (
            <button onClick={handleSendToSales} className="btn-send-sales">
              📤 Forward to Sales
            </button>
          )}
          {onEdit && (
            <button onClick={onEdit} className="btn-edit" style={{ background: 'linear-gradient(135deg, #ff9800, #f57c00)', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
              ✏️ Edit Quotation
            </button>
          )}
          <button onClick={handlePrint} className="btn-print">
            🖨️ Print
          </button>
          <button onClick={handleDownload} className="btn-download">
            📥 Download PDF
          </button>
          <button onClick={handlePrintItineraryOnly} className="btn-itinerary-only" style={{ background: 'linear-gradient(135deg, #00897b, #00695c)', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }}>
            📅 Itinerary Only PDF
          </button>
          <button onClick={onClose} className="btn-close">
            ✕ Close
          </button>
        </div>

        {/* A4 Pages Container */}
        <div className="quotation-pages">

          {/* PAGE 1: Cover Page with Package Image */}
          <div className="quotation-page">
            <div className="cover-page">
              {packageImage && (
                <div className="package-image-container">
                  <img src={packageImage} alt={destination} className="package-image" />
                </div>
              )}

              <div className="cover-content">
                <div className="company-header">
                  <h1>{companyInfo?.name || 'Gajanan World Tourism'}</h1>
                  <p className="tagline">Your Holiday Consultant</p>
                </div>

                <div className="quote-greeting">
                  <p>Dear <strong>{customerName}</strong>,</p>
                  <p>Greetings from <strong>{companyInfo?.name || 'Gajanan World Tourism'}</strong></p>
                  <p>Our sales team has put up this <strong>Quote</strong> regarding your upcoming trip. Please go through it and let's know if you would like any changes in any of the provided services. Contact details are provided at the end.</p>
                </div>

                <div className="trip-summary">
                  {packageName && (
                    <div className="summary-row" style={{ marginBottom: '15px' }}>
                      <div className="summary-item" style={{ width: '100%', textAlign: 'center' }}>
                        <label style={{ fontSize: '14px', color: '#6a1b9a', fontWeight: '700' }}>TOUR PACKAGE</label>
                        <p style={{ fontSize: '20px', fontWeight: '700', color: '#4a148c', marginTop: '5px' }}>{packageName}</p>
                      </div>
                    </div>
                  )}

                  <div className="summary-row">
                    <div className="summary-item">
                      <label>DESTINATION</label>
                      <p>{destination}</p>
                    </div>
                    <div className="summary-item">
                      <label>START DATE</label>
                      <p>{new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                    <div className="summary-item">
                      <label>DURATION</label>
                      <p>{duration}</p>
                    </div>
                  </div>

                  <div className="summary-row">
                    <div className="summary-item">
                      <label>PAX</label>
                      <p>{pax}</p>
                    </div>
                    <div className="summary-item">
                      <label>TRIP ID</label>
                      <p>{tripId}</p>
                    </div>
                  </div>
                </div>

                {/* Price Section - Per Person */}
                <div className="price-section">
                  <h3>PRICING</h3>
                  <div className="price-grid">
                    <div className="price-item">
                      <label>Per Adult (INR)</label>
                      <p className="price-total">₹{parseFloat(pricePerAdult || 0).toLocaleString('en-IN')} /-</p>
                      <span className="price-note">(per person, excluding GST)</span>
                    </div>
                    <div className="price-item">
                      <label>Per Child (INR)</label>
                      <p className="price-per-person">₹{parseFloat(pricePerChild || pricePerAdult || 0).toLocaleString('en-IN')} /-</p>
                      {parseFloat(airfarePerPerson) > 0 && (
                        <span className="price-note">Airfare: ₹{parseFloat(airfarePerPerson).toLocaleString('en-IN')} /- per person included</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PAGE 2: Hotels & Transportation */}
          <div className="quotation-page">
            <div className="page-header">
              <h2 className="section-title">Hotels / Accommodations</h2>
            </div>

            <div className="hotels-grid">
              {(() => {
                // Group hotels by name - same hotel shows once with all night numbers
                const grouped = [];
                hotels.forEach((hotel) => {
                  const existing = grouped.find(g => g.name === hotel.name && g.city === hotel.city);
                  if (existing) {
                    existing.nights.push(hotel.dayNumber);
                  } else {
                    grouped.push({ ...hotel, nights: [hotel.dayNumber] });
                  }
                });

                // Helper to format date
                const formatDate = (dateStr, dayOffset) => {
                  if (!dateStr) return '';
                  const d = new Date(dateStr);
                  d.setDate(d.getDate() + dayOffset);
                  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                };

                return grouped.map((hotel, index) => {
                  const firstNight = hotel.nights[0];
                  const lastNight = hotel.nights[hotel.nights.length - 1];
                  const checkInDate = formatDate(startDate, firstNight - 1);
                  const checkOutDate = formatDate(startDate, lastNight);

                  return (
                    <div key={index} className="hotel-card">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '10px' }}>
                        <div className="hotel-day-badge">
                          <span className="day-number">{hotel.nights.length > 1 ? `${firstNight}-${lastNight}` : firstNight}</span>
                          <span className="day-label">{hotel.nights.length > 1 ? 'Nights' : 'Night'}</span>
                        </div>
                        {startDate && (
                          <div style={{ background: '#f0f4ff', border: '2px solid #c5cae9', borderRadius: '10px', padding: '8px 16px', display: 'inline-block' }}>
                            <span style={{ fontSize: '15px', color: '#1a237e', fontWeight: '700' }}>📅 {checkInDate} → {checkOutDate}</span>
                          </div>
                        )}
                      </div>

                      <div className="hotel-details">
                        <div className="hotel-info">
                          <p className="hotel-night">{hotel.nights.length > 1 ? `${hotel.nights.length} Nights` : 'Night'} at <strong>{hotel.city}</strong></p>

                          <h3 className="hotel-name">{hotel.name}</h3>
                          <div className="hotel-rating">
                            {'★'.repeat(hotel.rating || 3)}{'☆'.repeat(5 - (hotel.rating || 3))}
                          </div>

                          <div className="hotel-room-details">
                            <div>
                              <label>ROOMS</label>
                              <p>{hotel.rooms}</p>
                              <p className="room-count">{hotel.roomCount}</p>
                            </div>
                            <div>
                              <label>MEAL PLAN</label>
                              <p>{hotel.mealPlan}</p>
                            </div>
                          </div>
                        </div>

                        {hotel.image && (
                          <div className="hotel-image">
                            <img src={hotel.image} alt={hotel.name} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Transportation Section - Removed: Transport details now part of daily itinerary */}
          </div>

          {/* PAGE 3 & 4: Day-wise Itinerary */}
          {itinerary.map((day, index) => {
            // Group itinerary into pages (2-3 days per page)
            if (index % 3 === 0) {
              const daysInPage = itinerary.slice(index, index + 3);
              console.log(`📄 Page ${Math.floor(index / 3) + 1} - Days in page:`, daysInPage.length, daysInPage);

              return (
                <div key={index} className="quotation-page">
                  <div className="page-header">
                    <h2 className="section-title">Day Wise Itinerary</h2>
                  </div>

                  {daysInPage.map((dayItem, dayIndex) => {
                    console.log(`  📅 Rendering Day ${dayItem.dayNumber}:`, dayItem);
                    return (
                      <div key={dayIndex} className="itinerary-day">
                        <div className="day-header">
                          <div className="day-badge">
                            <span className="day-number">{dayItem.dayNumber}</span>
                            <span className="day-label">Day</span>
                          </div>
                          <div className="day-title">
                            <h3>{dayItem.date}</h3>
                            <h2>{dayItem.title}</h2>

                            {/* From/To Locations */}
                            {(dayItem.from || dayItem.to) && (
                              <div style={{ display: 'flex', gap: '10px', marginTop: '5px', fontSize: '12px' }}>
                                {dayItem.from && (
                                  <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                    📍 From: {dayItem.from}
                                  </span>
                                )}
                                {dayItem.to && (
                                  <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                    📍 To: {dayItem.to}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Service Type & Category */}
                            {(dayItem.service || dayItem.category) && (
                              <div style={{ display: 'flex', gap: '10px', marginTop: '5px', fontSize: '11px' }}>
                                {dayItem.service && (
                                  <span style={{ background: '#f3e5f5', color: '#6a1b9a', padding: '2px 6px', borderRadius: '3px', fontWeight: '600' }}>
                                    🚗 {dayItem.service}
                                  </span>
                                )}
                                {dayItem.category && (
                                  <span style={{ background: '#fff3e0', color: '#e65100', padding: '2px 6px', borderRadius: '3px', fontWeight: '600' }}>
                                    ⭐ {dayItem.category}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="day-content">
                          {dayItem.image && (
                            <div className="day-image">
                              <img src={dayItem.image} alt={dayItem.title} />
                            </div>
                          )}
                          <div className="day-description">
                            {dayItem.description && (
                              <div
                                className="ql-content"
                                dangerouslySetInnerHTML={{
                                  __html: dayItem.description
                                    .replace(/^(<p>(<br>|&nbsp;|\s)*<\/p>\s*)+/i, '')
                                    .replace(/(<p>(<br>|&nbsp;|\s)*<\/p>\s*)+$/i, '')
                                }}
                                style={{
                                  fontSize: '12px',
                                  lineHeight: '1.7',
                                  color: '#333'
                                }}
                              />
                            )}

                            {/* Activities */}
                            {dayItem.activities && (
                              <div style={{ marginTop: '10px', padding: '8px', background: '#fffde7', borderLeft: '3px solid #f9a825', borderRadius: '4px' }}>
                                <p style={{ fontWeight: '600', fontSize: '12px', color: '#f57f17', marginBottom: '5px' }}>🎯 Activities:</p>
                                {typeof dayItem.activities === 'string' && dayItem.activities.includes('<') ? (
                                  <div
                                    className="ql-content"
                                    dangerouslySetInnerHTML={{ __html: dayItem.activities }}
                                    style={{ fontSize: '11px', color: '#827717' }}
                                  />
                                ) : (
                                  <p style={{ fontSize: '11px', color: '#827717' }}>
                                    {Array.isArray(dayItem.activities) ? dayItem.activities.join(', ') : dayItem.activities}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Transport Info */}
                            {(dayItem.transport || dayItem.from || dayItem.to) && (
                              <div style={{ marginTop: '10px', padding: '8px', background: '#e8eaf6', borderLeft: '3px solid #3f51b5', borderRadius: '4px' }}>
                                <p style={{ fontWeight: '600', fontSize: '12px', color: '#283593', marginBottom: '5px' }}>🚗 Transport:</p>
                                <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: '#1a237e', flexWrap: 'wrap' }}>
                                  {dayItem.transport && <span>🚌 {dayItem.transport}</span>}
                                  {dayItem.from && <span style={{ background: '#c8e6c9', padding: '2px 6px', borderRadius: '3px' }}>📍 From: {dayItem.from}</span>}
                                  {dayItem.to && <span style={{ background: '#bbdefb', padding: '2px 6px', borderRadius: '3px' }}>📍 To: {dayItem.to}</span>}
                                </div>
                              </div>
                            )}

                            {/* Cities */}
                            {dayItem.cities && (
                              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                                <span style={{ background: '#f3e5f5', color: '#6a1b9a', padding: '3px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                  🏙️ Cities: {Array.isArray(dayItem.cities) ? dayItem.cities.join(', ') : dayItem.cities}
                                </span>
                              </div>
                            )}

                            {/* Meals */}
                            {dayItem.meals && (dayItem.meals.breakfast || dayItem.meals.lunch || dayItem.meals.dinner) && (
                              <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {dayItem.meals.breakfast && (
                                  <span style={{ background: '#c8e6c9', color: '#2e7d32', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                                    🍳 Breakfast
                                  </span>
                                )}
                                {dayItem.meals.lunch && (
                                  <span style={{ background: '#c8e6c9', color: '#2e7d32', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                                    🍱 Lunch
                                  </span>
                                )}
                                {dayItem.meals.dinner && (
                                  <span style={{ background: '#c8e6c9', color: '#2e7d32', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                                    🍽️ Dinner
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Hotel Accommodation */}
                            {dayItem.accommodation && dayItem.accommodation.hotelName && (
                              <div style={{ marginTop: '10px', padding: '8px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                                <p style={{ fontSize: '11px', color: '#424242' }}>
                                  🏨 <strong>Hotel:</strong> {dayItem.accommodation.hotelName}
                                  {dayItem.accommodation.city && ` (${dayItem.accommodation.city})`}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            return null;
          })}

          {/* PAGE: Inclusions & Exclusions */}
          <div className="quotation-page">
            <div className="page-header">
              <h2 className="section-title">Inclusions/Exclusions</h2>
            </div>

            <div className="inclusions-section">
              <h3 className="subsection-title">Inclusions</h3>
              {inclusions.some(item => isHTML(item)) ? (
                <div
                  className="ql-content html-list-content"
                  dangerouslySetInnerHTML={{ __html: inclusions.join('') }}
                />
              ) : (
                <ul className="inclusion-list">
                  {inclusions.map((item, index) => (
                    <li key={index}>
                      <span className="bullet">●</span> {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="exclusions-section">
              <h3 className="subsection-title">Exclusions</h3>
              {exclusions.some(item => isHTML(item)) ? (
                <div
                  className="ql-content html-list-content exclusions-html"
                  dangerouslySetInnerHTML={{ __html: exclusions.join('') }}
                />
              ) : (
                <ul className="exclusion-list">
                  {exclusions.map((item, index) => (
                    <li key={index}>
                      <span className="bullet">●</span> {item}
                    </li>
                  ))}
                </ul>
              )}
              <p className="exclusion-note">
                <span className="warning-icon">⚠</span>
                Anything not mentioned in the inclusions is excluded.
              </p>
            </div>

            {/* Terms and Conditions */}
            <div className="terms-section">
              <h2 className="section-title">Terms and Conditions</h2>

              {termsAndConditions && termsAndConditions.length > 0 ? (
                <div className="payment-policy">
                  <h4>PAYMENT POLICY:</h4>
                  {termsAndConditions.some(t => isHTML(t)) ? (
                    <div
                      className="ql-content html-list-content"
                      dangerouslySetInnerHTML={{ __html: termsAndConditions.join('') }}
                    />
                  ) : (
                    <ul>
                      {termsAndConditions.map((term, index) => (
                        <li key={index}>{term}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="payment-policy">
                  <p style={{ color: '#999', fontStyle: 'italic' }}>No terms and conditions added.</p>
                </div>
              )}
            </div>

            {/* Important Notes */}
            {importantNotes && importantNotes.length > 0 && (
              <div style={{
                marginTop: '20px',
                border: '2px solid #f59e0b',
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#fffbeb'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  padding: '10px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '18px' }}>⚠️</span>
                  <h4 style={{ color: 'white', fontWeight: '700', fontSize: '14px', margin: 0 }}>IMPORTANT NOTES</h4>
                </div>
                <ul style={{ padding: '12px 18px 12px 36px', margin: 0 }}>
                  {importantNotes.map((note, index) => (
                    <li key={index} style={{
                      color: '#92400e',
                      fontSize: '12px',
                      marginBottom: '6px',
                      lineHeight: '1.5',
                      fontWeight: '500'
                    }}>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sales Person Details */}
            <div className="footer-section">
              <div className="sales-person">
                <p className="consultant-label">Your Holiday Consultant</p>
                <h3 className="consultant-name">
                  {/* Check multiple fields for sales person name */}
                  {salesPerson?.fullName ||
                    salesPerson?.displayName ||
                    (salesPerson?.name && !salesPerson?.name?.includes('@') ? salesPerson.name : null) ||
                    lead?.createdByName ||
                    lead?.assignedToName ||
                    lead?.salesPersonName ||
                    'Sales Team'}
                </h3>
                <p className="consultant-contact">{salesPerson?.phone || salesPerson?.mobile || ''}</p>
              </div>

              <div className="company-footer">
                <p><strong>{companyInfo?.name || 'Gajanan World Tourism'}</strong></p>
                <p>{companyInfo?.address || ''}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TourQuotationPDF;
